import { BCMSDate } from "@bcm-dev/bcms-vite";
import { Transaction } from "model";

/**
 * @class TransactionSearch
 * @classdesc Class definition for controlling the properties that are set in the search workflow
 * 
 * @property {STRING} _date - The date offset to filter by
 * @property {STRING} _description - The description to filter by
 * @property {STRING} _type - The type of transactions to filter by
 * @property {STRING} _amount - The amount to filter by
 * @property {STRING} _check - The check number to filter by
 * @property {Map} _ranges - The values and labels set in the custom select
 * @property {Map} _types - The values and labels set in the custom select
 */
export default class TransactionSearch {
    _type;
    _date;
    _description;
    _amount;
    _check;
    _accountID;

    _ranges = new Map([ 
        [ "-30", "Last 30 Days" ],
        [ "-60", "Last 60 Days" ],
        [ "-90", "Last 90 Days" ],
        [ "-120", "Last 120 Days" ],
        [ "custom", "Custom Range" ]
    ]);

    _types = new Map([ 
        [ "all", "All Transaction Types" ],
        [ "ach", "ACH" ],
        [ "atm", "ATM" ],
        [ "payment", "Bill Payment" ],
        [ "check", "Check" ],
        [ "deposit", "Deposit" ],
        [ "payroll", "Payroll" ],
        [ "withdrawal", "Withdrawal" ]
    ]);

    constructor(record) {
        this.date = record.date;
        this.description = record.description;
        this.type = record.type;
        this.amount = record.amount;
        this.check = record.check;
        this.accountID = record.accountID;
    }

    /**
     * @name date
     * @instance
     * @property set {STRING} - sets the date
     * @memberof TransactionSearch 
     */
    set date(value) { this._date = value; }
    /**
     * @name description
     * @instance
     * @property set {STRING} - sets the description
     * @memberof TransactionSearch
     */
    set description(value) { this._description = value; }
    /**
     * @name type
     * @instance
     * @property set {STRING} - sets the type
     * @memberof TransactionSearch
     */
    set type(value) { this._type = value; }
    /**
     * @name amount
     * @instance
     * @property set {STRING} - sets the amount
     * @memberof TransactionSearch
     */
    set amount(value) { this._amount = value; }
    /**
     * @name check
     * @instance
     * @property set {STRING} - sets the check number
     * @memberof TransactionSearch
     */
    set check(value) { this._check = value; }

    set accountID(value) { this._accountID = value; }

    /**
     * @name date
     * @instance
     * @property get {STRING} - returns the date
     * @memberof TransactionSearch
     */
    get date() { return this._date; }
    /**
     * @name description
     * @instance
     * @property get {STRING} - returns the description
     * @memberof TransactionSearch
     */
    get description() { return this._description; }
    /**
     * @name type
     * @instance
     * @property get {STRING} - returns the type
     * @memberof TransactionSearch
     */
    get type() { return this._type; }
    /**
     * @name amount
     * @instance
     * @property get {STRING} - returns the amount
     * @memberof TransactionSearch
     */
    get amount() { return this._amount; }
    /**
     * @name check
     * @instance
     * @property get {STRING} - returns the check number
     * @memberof TransactionSearch
     */
    get check() { return this._check; }

    get accountID() { return this._accountID; }

    /**
     * @name ranges
     * @instance
     * @property get {Map} - returns the ranges
     * @memberof TransactionSearch
     */
    get ranges() { return this._ranges; }
    /**
     * @name types
     * @instance
     * @property get {Map} - returns the types
     * @memberof TransactionSearch
     */
    get types() { return this._types; }

    /**
     * @name filters
     * @instance
     * @property get {OBJECT} - returns the set filter properties
     * @memberof TransactionSearch
     */
    get filters() {
        return Object.getOwnPropertyNames(this).reduce((result, name) => {
            const key = name.slice(1);
            const value = this[key];

            if(value !== undefined && value !== "" && (key !== "ranges" && key !== "types")){
                result[key] = value;
            }

            return result;
        }, {});
    }
    
    /**
     * @name record
     * @instance
     * @property get {OBJECT} - returns the set values
     * @memberof TransactionSearch
     */
    get record() {
        return {
            date: this.date,
            description: this.description,
            type: this.type,
            amount: this.amount,
            check: this.check,
            accountID: this.accountID
        };
    }

    checkValue(transaction, key) {
        switch(key) {
            case "date":
                return transaction.date.isAfter(BCMSDate.init().offset(this.date));
    
            case "description":
                return transaction.description.toLowerCase().indexOf(this.description.toLowerCase()) > -1;
    
            case "type":
                return this.type === "all" ? true : transaction.filterType === this.type;
    
            case "amount":
                const amount = parseFloat(transaction.amount.amount);
                return parseFloat(this.amount.amount) === (amount < 0 ? -amount : amount);
    
            case "check":
                const desc = transaction.description;
                return desc.toLowerCase().indexOf("check") > -1 && desc.indexOf(this.check) > -1;
    
            default:
                return true;
        }
    }

    filter() {
        return Transaction.initByAccount(this.accountID)
            .filter(transaction => {
                return Object.keys(this.filters).reduce((result, key) => {
                    if(!this.checkValue(transaction, key)) {
                        result = false;
                    }

                    return result;
                }, true);
            }, []);
    }

    reset() {
        return TransactionSearch.init({ accountID: this.accountID });
    }
    
    static init(record) {
        return new TransactionSearch({ type: "all", date: "-30", ...record ?? {} })
    }
}